import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FORM_ID = 'db69i9q2sgkaik1und10';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db69i9q2sgkaik1und10';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function About() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  useDocumentMeta({
    title: '关于我们 - 香草在线观看免费播放电视剧',
    description: '了解香草在线观看免费播放电视剧的平台定位、服务声明、联系方式与免责声明，我们致力于为追剧爱好者提供清晰便捷的高清电视剧浏览体验。',
    keywords: '关于香草视频,香草在线观看,免费播放电视剧,电视剧在线观看,联系方式',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = (formData.get('website_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value as string);
    });

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const success = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!success) {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后重试。',
        );
        return;
      }

      setStatus('success');
      formEl.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full bg-primary-50 border-b border-primary-100">
          <div className="w-full px-4 md:px-8 py-10 md:py-16">
            <nav className="text-xs md:text-sm text-foreground-500 flex items-center gap-1.5 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">关于我们</h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl">
              香草在线观看免费播放电视剧，是面向追剧爱好者的电视剧内容聚合平台，帮助你在海量剧集中快速找到想看的高清剧集。
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-10 md:py-14 flex flex-col gap-10 md:gap-14">
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">网站定位</h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                香草视频以“看得清、找得到、追得顺”为目标，专注电视剧内容的浏览与汇总。我们把都市、古装、悬疑、爱情、
                科幻、家庭、动作、喜剧、历史等多种类型整理成清晰的分类，提供按类型、地区、年份的多条件筛选，
                以及关键词搜索、热度与更新时间排序，让你不再为找剧而烦恼。
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                同时，网站支持收藏关注功能，你可以把想追的剧集标记下来，方便随时回访。所有内容均围绕“追剧”这一核心场景设计，
                力求界面简洁、层级清晰、阅读舒适。
              </p>
            </div>
            <div className="lg:col-span-1 rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-hand-heart-line text-primary-600" />
                服务声明
              </h3>
              <ul className="space-y-3 text-sm text-foreground-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5" />
                  提供电视剧资料的浏览、搜索与筛选服务。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5" />
                  收藏功能基于浏览器本地保存，方便你标记关注。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-accent-600 mt-0.5" />
                  播放功能仅为界面演示，不提供真实视频内容。
                </li>
              </ul>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1">
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">联系我们</h2>
              <div className="rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
                <div className="flex items-center gap-3 px-4 py-4 text-sm">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <p className="text-foreground-500 text-xs">联系邮箱</p>
                    <p className="text-foreground-900">contact@vanilla-tv-demo.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-4 text-sm">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-time-line" />
                  </span>
                  <div>
                    <p className="text-foreground-500 text-xs">反馈时间</p>
                    <p className="text-foreground-900">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-4 text-sm">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-chat-smile-2-line" />
                  </span>
                  <div>
                    <p className="text-foreground-500 text-xs">反馈入口</p>
                    <p className="text-foreground-900">填写右侧表单给我们留言</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">意见反馈</h2>
              <form
                data-readdy-form
                id={FORM_ID}
                onSubmit={handleSubmit}
                className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="feedback-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="feedback-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">反馈类型</label>
                  <select
                    id="feedback-subject"
                    name="subject"
                    defaultValue="内容建议"
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="商务合作">商务合作</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="feedback-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的意见或建议（最多 500 字）"
                    className="w-full px-3 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="vanilla-extra-field"
                />

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        提交反馈
                      </>
                    )}
                  </button>
                  {status === 'success' && (
                    <p className="text-sm text-primary-700 flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line" />
                      提交成功，感谢你的反馈！
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="text-sm text-accent-700 flex items-center gap-1.5">
                      <i className="ri-error-warning-line" />
                      {formError}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </section>

          <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-8">
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">版权信息与免责声明</h2>
            <div className="space-y-3 text-sm leading-relaxed text-foreground-600">
              <p>
                本网站为演示性质的内容平台。站内出现的所有电视剧名称、海报、简介、演职人员、评分与更新信息均为虚拟示例数据，
                仅用于展示产品设计与交互效果，不指向任何真实影视作品，也不构成任何形式的观看或下载承诺。
              </p>
              <p>
                本网站不存储、不制作、不传播任何影视内容，播放功能仅作界面演示，无法播放真实视频。如本站展示的任何内容无意中侵犯了你的权益，
                请通过上方邮箱联系我们，我们会尽快处理。
              </p>
              <p>© {new Date().getFullYear()} 香草视频 · 本站保留对演示内容与页面设计的说明权。</p>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 border border-background-300 hover:border-primary-300 text-sm font-medium text-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}